import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type KeyboardEvent, ReactNode } from 'react';
import { FieldError, Group, Input, Label, Text, TextField, TextFieldProps } from 'react-aria-components';
import { cn } from './utils';

export const agInputWrapperVariants = cva(
  'inline-flex items-center w-full bg-white border transition-all duration-150',
  {
    variants: {
      variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
      size: {
        sm: 'h-[32px] px-2.5 gap-1.5 text-14',
        md: 'h-[40px] px-3 gap-2 text-14',
        lg: 'h-[48px] px-4 gap-2.5 text-16',
      },
      shape: { rounded: 'rounded-md', pill: 'rounded-full' },
      isFocused: { true: '', false: '' },
      isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
    },
    compoundVariants: [
      { variant: 'default', isFocused: true, className: 'border-brand-6 ring-2 ring-ring-primary' },
      { variant: 'error', isFocused: true, className: 'ring-2 ring-ring-destructive' },
    ],
  }
);

export const agInputVariants = cva(
  'flex-1 bg-transparent outline-none text-neutral-text placeholder:text-neutral-alpha-7 min-w-0',
  { variants: { size: { sm: 'text-14', md: 'text-14', lg: 'text-16' } } }
);

const agLabelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
});

type AgInputVariantProps = VariantProps<typeof agInputWrapperVariants>;

export interface AgInputProps extends Omit<TextFieldProps, 'children'> {
  /** Label text displayed above the input */
  label?: string;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  /** Show "(optional)" indicator next to label */
  isOptional?: boolean;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message to display (also sets error state) */
  errorMessage?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Content to render before the input (icons, badges, etc.) */
  startContent?: ReactNode;
  /** Content to render after the input (icons, buttons, etc.) */
  endContent?: ReactNode;
  /** Size variant */
  size?: AgInputVariantProps['size'];
  /** Shape variant */
  shape?: AgInputVariantProps['shape'];
  /** Additional class for the wrapper container */
  wrapperClassName?: string;
  /** Additional class for the input element */
  inputClassName?: string;
  /** HTML input type */
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search';
  /** Keyboard event handler for the input element */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Test id forwarded to the underlying input element */
  'data-testid'?: string;
}

export const AgInput = forwardRef<HTMLInputElement, AgInputProps>(
  (
    {
      label,
      isRequired,
      isOptional,
      helperText,
      errorMessage,
      placeholder,
      startContent,
      endContent,
      size = 'md',
      shape = 'rounded',
      className,
      wrapperClassName,
      inputClassName,
      isDisabled,
      isInvalid,
      type = 'text',
      onKeyDown,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => {
    // If errorMessage is provided, the input is invalid
    const hasError = isInvalid || !!errorMessage;

    return (
      <TextField
        className={cn('flex flex-col gap-2 w-full', className)}
        isDisabled={isDisabled}
        isInvalid={hasError}
        {...props}
      >
        {({ isFocused, isDisabled: isFieldDisabled }) => (
          <>
            {label && (
              <Label className={cn(agLabelVariants({ size }))}>
                {label}
                {isRequired && <span className="text-red-text ml-2">*</span>}
                {isOptional && <span className="text-neutral-alpha-7 font-normal ml-4">(optional)</span>}
              </Label>
            )}
            <Group
              className={cn(
                agInputWrapperVariants({
                  variant: hasError ? 'error' : 'default',
                  size,
                  shape,
                  isFocused,
                  isDisabled: isFieldDisabled,
                }),
                wrapperClassName
              )}
            >
              {startContent && <span className="flex items-center shrink-0 text-neutral-alpha-8">{startContent}</span>}
              <Input
                ref={ref}
                type={type}
                placeholder={placeholder}
                className={cn(agInputVariants({ size }), inputClassName)}
                onKeyDown={onKeyDown}
                data-testid={dataTestId}
              />
              {endContent && <span className="flex items-center shrink-0 text-neutral-alpha-8">{endContent}</span>}
            </Group>
            {errorMessage && <FieldError className="text-12 text-red-text">{errorMessage}</FieldError>}
            {helperText && !errorMessage && (
              <Text slot="description" className="text-12 text-neutral-alpha-7">
                {helperText}
              </Text>
            )}
          </>
        )}
      </TextField>
    );
  }
);

AgInput.displayName = 'AgInput';
